import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import { unformatCPF, formatCPF, isValidCPF } from '../../utils/cpfUtils';
import CPFMaskInput from '../masks/CPFMaskInput';

function UsuarioModal({ show, onClose, onSaveSuccess, usuario = null }) {
  const isEditing = !!usuario;

  const [formData, setFormData] = useState({
    cpf: '',
    nome: '',
    login: '',
    senha: '',
    situacao: '',
    role: '',
    cargo: '',
    email: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [emailError, setEmailError] = useState('');

  const token = localStorage.getItem('token');

  // Resetar formulário quando o modal abrir/fechar
  useEffect(() => {
    if (show) {
      if (isEditing && usuario) {
        setFormData({
          cpf: usuario.cpf || '',
          nome: usuario.nome || '',
          login: usuario.login || '',
          senha: usuario.senha || '',
          situacao: usuario.situacao || '',
          role: usuario.role || '',
          cargo: usuario.cargo || '',
          email: usuario.email || '',
        });
      } else {
        setFormData({
          cpf: '',
          nome: '',
          login: '',
          senha: '',
          situacao: '',
          role: '',
          cargo: '',
          email: '',
        });
      }
      setErrors({});
      setEmailError('');
    }
  }, [show, usuario, isEditing]);

  const handleInputChange = e => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }

    // Validação de e-mail em tempo real
    if (name === 'email') {
      validateEmail(value);
    }
  };

  const validateEmail = email => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError('');
    } else if (!re.test(email)) {
      setEmailError('E-mail inválido');
    } else {
      setEmailError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.cpf.trim()) {
      newErrors.cpf = 'CPF é obrigatório';
    } else if (!isValidCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inválido';
    }

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.login.trim()) {
      newErrors.login = 'Login é obrigatório';
    }

    if (!isEditing && !formData.senha.trim()) {
      newErrors.senha = 'Senha é obrigatória';
    }

    if (!formData.situacao.trim()) {
      newErrors.situacao = 'Situação é obrigatória';
    }

    if (!formData.role.trim()) {
      newErrors.role = 'Tipo de usuário é obrigatório';
    }

    if (formData.email && emailError) {
      newErrors.email = emailError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const usuarioParaSalvar = {
        ...formData,
        cpf: unformatCPF(formData.cpf),
      };
      console.log('Dados para salvar:', usuarioParaSalvar);
      const response = await axios.post(
        `${API_BASE_URL.USUARIO}/salvar`,
        usuarioParaSalvar,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Chama callback de sucesso
      if (onSaveSuccess) {
        onSaveSuccess(response.data, isEditing);
      }

      // Fecha o modal
      handleClose();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);

      // Tratar erros de validação do backend
      if (error.response?.status === 400) {
        setErrors({ submit: 'Dados inválidos. Verifique os campos.' });
      } else {
        setErrors({
          submit: `Erro ao ${
            isEditing ? 'atualizar' : 'cadastrar'
          } usuário. Tente novamente.`,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      cpf: '',
      nome: '',
      login: '',
      senha: '',
      situacao: '',
      role: '',
      cargo: '',
      email: '',
    });
    setErrors({});
    setEmailError('');
    setIsLoading(false);
    onClose();
  };

  if (!show) return null;

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div
            className="modal-header bg-success text-white"
            style={{ height: '30px' }}
          >
            <h5 className="modal-title">
              <i className="bi bi-person-plus me-2"></i>
              {isEditing ? 'Editar Usuário' : 'Novo Usuário'}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleClose}
              disabled={isLoading}
            ></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body small">
              {errors.submit && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {errors.submit}
                </div>
              )}

              <div className="row g-2">
                <div className="col-md-3 input-group-sm">
                  <label className="form-label mb-1">
                    CPF <span className="text-danger">*</span>
                  </label>
                  <CPFMaskInput
                    value={formatCPF(formData.cpf)}
                    onChange={e =>
                      handleInputChange({
                        target: {
                          name: 'cpf',
                          value: unformatCPF(e.target.value),
                        },
                      })
                    }
                    id="cpf"
                    name="cpf"
                    type="text"
                    disabled={isLoading}
                    className={`form-control ${errors.cpf ? 'is-invalid' : ''}`}
                  />
                  {errors.cpf && (
                    <div className="invalid-feedback">{errors.cpf}</div>
                  )}
                </div>

                <div className="col-md-9 input-group-sm">
                  <label className="form-label mb-1">
                    Nome <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.nome ? 'is-invalid' : ''
                    }`}
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                  />
                  {errors.nome && (
                    <div className="invalid-feedback">{errors.nome}</div>
                  )}
                </div>
              </div>

              <div className="row g-2 mt-2">
                <div className="col-md-3 input-group-sm">
                  <label className="form-label mb-1">
                    Login <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.login ? 'is-invalid' : ''
                    }`}
                    name="login"
                    value={formData.login}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                  />
                  {errors.login && (
                    <div className="invalid-feedback">{errors.login}</div>
                  )}
                </div>

                <div className="col-md-3 input-group-sm">
                  <label className="form-label mb-1">
                    Senha {!isEditing && <span className="text-danger">*</span>}
                  </label>
                  <input
                    type="password"
                    className={`form-control ${
                      errors.senha ? 'is-invalid' : ''
                    }`}
                    name="senha"
                    value={formData.senha}
                    onChange={handleInputChange}
                    disabled={isLoading}

                    placeholder={
                      isEditing ? 'Deixe em branco para manter atual' : ''
                    }
                  />
                  {errors.senha && (
                    <div className="invalid-feedback">{errors.senha}</div>
                  )}
                </div>

                <div className="col-md-3 input-group-sm">
                  <label className="form-label mb-1">
                    Situação <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${
                      errors.situacao ? 'is-invalid' : ''
                    }`}
                    name="situacao"
                    value={formData.situacao}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                  >
                    <option value="">Selecione...</option>
                    <option value="ATIVO">ATIVO</option>
                    <option value="INATIVO">INATIVO</option>
                  </select>
                  {errors.situacao && (
                    <div className="invalid-feedback">{errors.situacao}</div>
                  )}
                </div>

                <div className="col-md-3 input-group-sm">
                  <label className="form-label mb-1">
                    Tipo de Usuário <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.role ? 'is-invalid' : ''}`}
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                  >
                    <option value="">Selecione...</option>
                    <option value="ADMIN">ADMINISTRADOR</option>
                    <option value="GEST">GESTOR</option>
                    <option value="USER">USUÁRIO</option>
                  </select>
                  {errors.role && (
                    <div className="invalid-feedback">{errors.role}</div>
                  )}
                </div>
              </div>

              <div className="row g-2 mt-2">
                <div className="col-md-6 input-group-sm">
                  <label className="form-label mb-1">E-mail</label>
                  <input
                    type="email"
                    className={`form-control ${
                      errors.email || emailError ? 'is-invalid' : ''
                    }`}
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  {(errors.email || emailError) && (
                    <div className="invalid-feedback">
                      {errors.email || emailError}
                    </div>
                  )}
                </div>

                <div className="col-md-6 input-group-sm">
                  <label className="form-label mb-1">Cargo / Função</label>
                  <input
                    type="text"
                    className="form-control"
                    name="cargo"
                    value={formData.cargo}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleClose}
                disabled={isLoading}
              >
                <i className="bi bi-x-lg me-1" />
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-success btn-sm"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-1" />
                    {isEditing ? 'Atualizar' : 'Salvar'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default UsuarioModal;
